import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";

/* Signature city mini-games. mode: "trevi" (coin-toss timing) | "glide"|"surf"|"dune" (runner).
   onWin(coins) is called when the player collects their reward. */
const MODES = {
  trevi: { kind: "toss", title: "Trevi Coin Toss", sky: "#0e7490", accent: "#67e8f9", tip: "Tap when the marker is over the glowing water. 5 wishes!" },
  glide: { kind: "run", title: "Hang-Glide off Sugarloaf", sky: "#7dd3fc", ground: "#0e7490", hero: "🪂", bad: "🦅", tip: "Tap to rise. Grab coins, dodge the birds — 25s!" },
  surf: { kind: "run", title: "Surf Bondi Beach", sky: "#38bdf8", ground: "#0369a1", hero: "🏄", bad: "🌊", tip: "Tap to hop. Grab coins, dodge the big sets — 25s!" },
  dune: { kind: "run", title: "Camel Dune Dash", sky: "#fbbf24", ground: "#b45309", hero: "🐫", bad: "🌵", tip: "Tap to leap the dunes, dodge cacti — 25s!" },
};

export default function CityMiniGame({ open, mode, onClose, onWin }) {
  const cfg = MODES[mode] || MODES.glide;
  const [phase, setPhase] = useState("intro");
  const [score, setScore] = useState(0);
  const canvasRef = useRef(null);
  const stateRef = useRef({});
  const rafRef = useRef(0);

  useEffect(() => { if (open) { setPhase("intro"); setScore(0); } return () => cancelAnimationFrame(rafRef.current); }, [open, mode]);

  const finish = (s) => { cancelAnimationFrame(rafRef.current); setScore(s); setPhase("done"); };

  const start = () => {
    setPhase("play");
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d"); const W = cv.width, H = cv.height;
    let last = performance.now();
    if (cfg.kind === "run") {
      const S = { y: H / 2, vy: 0, coins: 0, lives: 3, t: 25, items: [], spawn: 0, hurt: 0, hero: cfg.hero, bad: cfg.bad };
      stateRef.current = S;
      const loop = (now) => {
        const dt = Math.min(0.05, (now - last) / 1000); last = now;
        S.t -= dt; if (S.t <= 0 || S.lives <= 0) { finish(S.coins * 3); return; }
        S.vy += 900 * dt; S.y += S.vy * dt;
        if (S.y > H - 26) { S.y = H - 26; S.vy = 0; } if (S.y < 20) { S.y = 20; S.vy = 0; }
        S.spawn -= dt; if (S.spawn <= 0) { S.spawn = 0.55 + Math.random() * 0.5; const coin = Math.random() < 0.62; S.items.push({ x: W + 20, y: 40 + Math.random() * (H - 90), coin, hit: false }); }
        const speed = 210 + (25 - S.t) * 6;
        for (const it of S.items) it.x -= speed * dt;
        S.items = S.items.filter((it) => it.x > -30);
        for (const it of S.items) { if (!it.hit && Math.abs(it.x - 90) < 24 && Math.abs(it.y - S.y) < 26) { it.hit = true; if (it.coin) S.coins++; else { S.lives--; S.hurt = 0.4; } } }
        if (S.hurt > 0) S.hurt -= dt;
        // draw
        ctx.fillStyle = cfg.sky; ctx.fillRect(0, 0, W, H);
        ctx.fillStyle = cfg.ground; ctx.fillRect(0, H - 16, W, 16);
        ctx.font = "22px serif"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        for (const it of S.items) { if (it.hit && it.coin) continue; ctx.font = it.coin ? "20px serif" : "24px serif"; ctx.fillText(it.coin ? "🪙" : S.bad, it.x, it.y); }
        ctx.globalAlpha = S.hurt > 0 ? 0.4 : 1; ctx.font = "28px serif"; ctx.fillText(S.hero, 90, S.y); ctx.globalAlpha = 1;
        ctx.fillStyle = "#fff"; ctx.font = "bold 14px monospace"; ctx.textAlign = "left"; ctx.textBaseline = "top";
        ctx.fillText(`🪙 ${S.coins}`, 10, 8); ctx.fillText(`❤ ${S.lives}`, 100, 8); ctx.textAlign = "right"; ctx.fillText(`${Math.ceil(S.t)}s`, W - 10, 8);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } else {
      const S = { mx: 0, dir: 1, tw: 0.42, wishes: 5, hits: 0, splash: 0, targetC: 0.5 };
      stateRef.current = S;
      const loop = (now) => {
        const dt = Math.min(0.05, (now - last) / 1000); last = now;
        if (S.wishes <= 0) { finish(S.hits * 12); return; }
        S.mx += S.dir * dt * 0.95; if (S.mx > 1) { S.mx = 1; S.dir = -1; } if (S.mx < 0) { S.mx = 0; S.dir = 1; }
        if (S.splash > 0) S.splash -= dt;
        const W = cv.width, H = cv.height;
        ctx.fillStyle = "#082f49"; ctx.fillRect(0, 0, W, H);
        // fountain basin
        ctx.fillStyle = "#0e7490"; ctx.fillRect(20, H - 90, W - 40, 70);
        // target glow
        const tcx = S.targetC * (W - 40) + 20, tw = S.tw * (W - 40);
        ctx.fillStyle = cfg.accent; ctx.globalAlpha = 0.5 + (S.splash > 0 ? 0.4 : 0.2 * Math.sin(now / 150));
        ctx.fillRect(tcx - tw / 2, H - 90, tw, 70); ctx.globalAlpha = 1;
        // marker
        const mxpx = S.mx * (W - 40) + 20;
        ctx.strokeStyle = "#fde047"; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(mxpx, 24); ctx.lineTo(mxpx, H - 96); ctx.stroke();
        ctx.font = "22px serif"; ctx.textAlign = "center"; ctx.fillText("🪙", mxpx, 16);
        if (S.splash > 0) { ctx.font = "26px serif"; ctx.fillText("💦", tcx, H - 60); }
        ctx.fillStyle = "#fff"; ctx.font = "bold 14px monospace"; ctx.textAlign = "left"; ctx.textBaseline = "top";
        ctx.fillText(`Wishes ${S.wishes}`, 10, 8); ctx.textAlign = "right"; ctx.fillText(`Hits ${S.hits}`, W - 10, 8);
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    }
  };

  const tap = () => {
    if (phase !== "play") return;
    const S = stateRef.current;
    if (cfg.kind === "run") { S.vy = -330; }
    else {
      const d = Math.abs(S.mx - S.targetC);
      if (d < S.tw / 2) { S.hits++; S.splash = 0.5; S.tw = Math.max(0.16, S.tw - 0.05); S.targetC = 0.2 + Math.random() * 0.6; }
      S.wishes--;
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-slate-950/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="city-minigame">
      <div className="w-full max-w-md border-2 border-cyan-400/60 bg-[#070a1c] shadow-[0_0_40px_rgba(34,211,238,0.3)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-cyan-400/20">
          <p className="cq-px text-cyan-300 text-[10px]">{cfg.title}</p>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="city-minigame-close"><X size={16} /></button>
        </div>
        <div className="p-4">
          <div className="relative mx-auto" style={{ maxWidth: 460 }}>
            <canvas ref={canvasRef} width={460} height={300} onPointerDown={tap} className="w-full border border-slate-700 rounded touch-none cursor-pointer bg-slate-900" data-testid="city-minigame-canvas" />
            {phase === "intro" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/80 rounded">
                <p className="text-slate-200 text-sm text-center px-6">{cfg.tip}</p>
                <button onClick={start} className="cq-px cq-menu-btn text-cyan-200 px-6" data-testid="city-minigame-start">▶ PLAY</button>
              </div>
            )}
            {phase === "done" && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950/85 rounded">
                <p className="cq-px text-amber-300 text-sm">{score > 0 ? `+${score} COINS!` : "SO CLOSE!"}</p>
                <p className="text-slate-300 text-xs">{score > 0 ? "Nice moves." : "Give it another go?"}</p>
                <div className="flex gap-2">
                  <button onClick={() => { setPhase("intro"); }} className="cq-px cq-menu-btn text-slate-200 px-4" data-testid="city-minigame-again">↻ AGAIN</button>
                  <button onClick={() => onWin(score)} className="cq-px cq-menu-btn text-amber-200 px-4" data-testid="city-minigame-collect">COLLECT</button>
                </div>
              </div>
            )}
          </div>
          {phase === "play" && <p className="text-center text-[11px] text-slate-400 mt-2">Tap the screen!</p>}
        </div>
      </div>
    </div>
  );
}
